import { HttpClientModule } from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, provideNgReflectAttributes, provideZoneChangeDetection } from '@angular/core';
import { LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalModule,
	MsalRedirectComponent,
	MsalService,
} from '@azure/msal-angular';
import { IPublicClientApplication, InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { QRCodeComponent } from 'angularx-qrcode';
import { CookieService } from 'ngx-cookie-service';
import { MarkdownModule } from 'ngx-markdown';
import { MessageService } from 'primeng/api';
import { ConfirmationService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { loginRequest, msalConfig, protectedResources } from './auth-config';
import { AuthComponent } from './core/components/auth/auth.component';
import { primeNGThemeConfig } from './core/config/theme.config';
import { PRIMENG_ROOT_MODULE } from './core/constants/primeng-root-import-modules.constant';
import { CoreModule } from './core/core.module';
import { RequestInterceptor } from './core/interceptors';
import { ApiService } from './core/services/api.service';
import { AppContextService } from './core/services/app-context.service';
import { CacheService } from './core/services/cache.service';
import { LocaleService } from './core/services/locale.service';
import { NavigationService } from './core/services/navigation.service';
import { SignalRService } from './core/services/signalr.service';
import { ThemeService } from './core/services/theme.service';
import { UtilService } from './core/services/util.service';
import { AccessDeniedComponent } from './features/access/access-denied/access-denied.component';
import { RecordNotFoundComponent } from './features/access/record-not-found/record-not-found.component';
import { ServiceNotAccessibleComponent } from './features/access/service-not-accessible/service-not-accessible.component';
import { SystemAvailabilityApiService } from './features/access/services/system-availability-api.service';
import { Globals } from './globals';
import { FooterComponent } from './layout/footer/footer.component';
import { HeaderComponent } from './layout/header/header.component';
import { MainComponent } from './layout/main/main.component';
import { SystemBannerService } from './layout/services/system-banner.service';
import { GlobalSearchBarComponent } from './layout/sidebar/global-search-bar/global-search-bar.component';
import { SidebarComponent } from './layout/sidebar/sidebar.component';
import { SubMenuComponent } from './layout/sub-menu/sub-menu.component';
import { SystemBannerComponent } from './layout/system-banner/system-banner.component';
import { SharedModule } from './shared/shared.module';

/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	const protectedResourceMap = new Map<string, Array<string>>();

	protectedResourceMap.set(protectedResources.spyderFlowApi.endpoint, protectedResources.spyderFlowApi.scopes);

	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap,
	};
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: loginRequest,
	};
}

@NgModule({
	imports: [
		BrowserModule,
		FormsModule,
		ReactiveFormsModule,
		...PRIMENG_ROOT_MODULE,
		AppRoutingModule,
		HttpClientModule,
		BrowserAnimationsModule,
		CoreModule,
		SharedModule,
		MsalModule,
		MarkdownModule.forRoot(),
		QRCodeComponent,
	],
	declarations: [
		AuthComponent,
		AppComponent,
		SubMenuComponent,
		MainComponent,
		SidebarComponent,
		HeaderComponent,
		FooterComponent,
		SystemBannerComponent,
		AccessDeniedComponent,
		RecordNotFoundComponent,
		ServiceNotAccessibleComponent,
		GlobalSearchBarComponent,
	],
	providers: [
		AppContextService,
		Globals,
		ApiService,
		LocaleService,
		SignalRService,
		CacheService,
		UtilService,
		ThemeService,
		MessageService,
		CookieService,
		ConfirmationService,
		DialogService,
		NavigationService,
		SystemBannerService,
		// 1. Enable debugging attributes (New in Angular 20+)
		provideNgReflectAttributes(),

		// 2. Configure change detection coalescing
		provideZoneChangeDetection({ eventCoalescing: true }),
		SystemAvailabilityApiService,
		{
			provide: HTTP_INTERCEPTORS,
			useClass: RequestInterceptor,
			multi: true,
		},
		{
			provide: HTTP_INTERCEPTORS,
			useClass: MsalInterceptor,
			multi: true,
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory,
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory,
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory,
		},
		MsalService,
		MsalGuard,
		MsalBroadcastService,
		{
			provide: LOCALE_ID,
			useFactory: (localeService: LocaleService) => {
				localeService.registerCulture();
			},
			deps: [LocaleService],
		},
		providePrimeNG({
			theme: primeNGThemeConfig,
			pt: {
				button: {
					root: {
						class: 'p-button-sm',
					},
				},
				checkbox: {
					root: {
						class: 'p-checkbox-sm',
					},
				},
				autoComplete: {
					root: {
						class: 'p-autoComplete-sm',
					},
				},
				textarea: {
					root: {
						class: 'p-textarea-sm',
					},
				},
				multiselect: {
					root: {
						class: 'p-multiselect-sm',
					},
				},
				selectbutton: {
					root: {
						class: 'p-selectbutton-sm',
					},
				},
				splitbutton: {
					root: {
						class: 'p-splitbutton-sm',
					},
				},
				togglebutton: {
					root: {
						class: 'p-togglebutton-sm',
					},
				},
				select: {
					root: {
						class: 'p-select-sm',
					},
				},
				inputtext: {
					root: {
						class: 'p-inputtext-sm',
					},
				},
				inputnumber: {
					root: {
						class: 'p-inputnumber-sm',
					},
					input: {
						class: 'p-inputtext-sm',
					},
				},
			},
		}),
	],
	bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
